<script setup lang="ts">
/**
 * VideoPage —— 成果视频独立展示页面（A，Option A）。
 *
 * 职责：展示成果视频专栏，左侧为固定的“成果视频”标题侧栏，右侧渲染面包屑导航与大号视频播放器。
 * 直接利用 Store Aggregate 中 type='video' 的 Section 数据。
 *
 * **状态分支**（对齐 DetailPage）：
 *   - isLoading           → 骨架屏占位
 *   - isCritical          → CriticalFailureBanner
 *   - fallback            → 详情级容错「内容不存在」
 *   - 正常加载 (Success)   → 渲染分栏布局与视频播放器
 *
 * **视频加载容错**：原生 `<video>` 绑定 `@error` 自适应切到“视频暂不可用”提示状态。
 */
import { computed, onMounted } from 'vue';
import { RouterLink } from 'vue-router';

import CriticalFailureBanner from '@/components/CriticalFailureBanner.vue';
import PreviewBanner from '@/components/PreviewBanner.vue';
import { useShowcase } from '@/store/use-showcase';
import { useAssetFallback } from '@/composables/use-asset-fallback';
import { copy } from '@/i18n/zh-CN';
import { hasText, posterFor } from '@/registry/sections/section-presenters';

const store = useShowcase();

onMounted(() => {
  if (store.state.status === 'idle') {
    void store.bootstrap();
  }
});

const section = computed(() => {
  if (store.state.aggregate === null) {
    return undefined;
  }
  return store.state.aggregate.sections.find((s) => s.type === 'video');
});

const isPreview = computed(() => store.state.isPreview);
const isLoading = computed(
  () => store.state.status === 'idle' || store.state.status === 'loading',
);
const isCritical = computed(() => store.state.status === 'failed');

const fallback = computed(() => {
  if (isLoading.value || isCritical.value) {
    return null;
  }
  const sec = section.value;
  if (sec === undefined) {
    return { reason: 'not_found' };
  }
  if (sec.publishStatus !== 'Published' && !isPreview.value) {
    return { reason: 'unpublished' };
  }
  return null;
});

const { failed: videoFailed, handleError: handleVideoError } = useAssetFallback(
  'achievement-video-page',
);

const poster = computed(() => (section.value ? posterFor(section.value) : undefined));
const hasVideo = computed(() => section.value !== undefined && hasText(section.value.videoUrl));
</script>

<template>
  <main :class="$style.page" data-testid="achievement-video-page">
    <PreviewBanner v-if="isPreview" />

    <div
      v-if="isLoading"
      :class="$style.skeleton"
      data-testid="video-page-skeleton"
    >
      {{ copy.app.loading }}
    </div>

    <CriticalFailureBanner
      v-else-if="isCritical"
      :error="store.state.error"
    />

    <div
      v-else-if="fallback !== null"
      :class="$style.fallback"
      data-testid="video-page-fallback"
      role="alert"
    >
      <h1 :class="$style.fallbackTitle">{{ copy.fallback.detail.title }}</h1>
      <p :class="$style.fallbackDesc">{{ copy.fallback.detail.description }}</p>
      <RouterLink :class="$style.fallbackLink" to="/">{{ copy.detail.backHome }}</RouterLink>
    </div>

    <template v-else>
      <div :class="$style.layout" data-shell="content-layout">
        <!-- Sidebar -->
        <aside :class="$style.sidebar" data-testid="video-sidebar">
          <div :class="$style.sidebarTitle">
            {{ copy.detail.achievementVideo }}
          </div>
        </aside>

        <!-- Main Column -->
        <div :class="$style.main" data-shell="main-col">
          <!-- Video Wrapper -->
          <section
            v-if="section !== undefined"
            :class="$style.videoSection"
            data-shell="detail-section"
            data-testid="video-content"
          >
            <video
              v-if="hasVideo && !videoFailed"
              :class="$style.video"
              :poster="poster"
              :src="section.videoUrl ?? undefined"
              controls
              preload="metadata"
              playsinline
              @error="handleVideoError"
            />
            <p v-else :class="$style.videoUnavailable" role="status">
              {{ copy.fallback.asset.videoUnavailable }}
            </p>
          </section>
        </div>
      </div>
    </template>
  </main>
</template>

<style module>
.page {
  min-height: 60vh;
  padding-bottom: var(--space-6);
}

.layout {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 var(--space-3);
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
}

.sidebar {
  width: var(--detail-sidebar-width);
  flex: 0 0 var(--detail-sidebar-width);
  background: var(--color-bg-primary);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  overflow: hidden;
  position: sticky;
  top: var(--space-3);
}

.sidebarTitle {
  padding: var(--space-2) var(--space-3);
  background: var(--color-brand-primary);
  color: var(--color-bg-primary);
  font-size: var(--font-size-md);
  font-weight: 700;
  text-align: center;
}

.main {
  flex: 1 1 auto;
  min-width: 0;
}
.videoSection {
  background: var(--color-bg-primary);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  padding: var(--space-5);
}

.video {
  width: 100%;
  display: block;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  background: var(--color-bg-secondary);
}

.videoUnavailable {
  margin: 0;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-5);
  color: var(--color-text-muted);
  font-size: var(--font-size-base);
  text-align: center;
}

.skeleton {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-5) var(--space-3);
  background: var(--color-bg-secondary);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-md);
  text-align: center;
}

.fallback {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: var(--space-6) var(--space-3);
  text-align: center;
}

.fallbackTitle {
  color: var(--color-brand-primary);
  font-size: var(--font-size-xl);
  margin-bottom: var(--space-2);
}

.fallbackDesc {
  color: var(--color-text-secondary);
  margin-bottom: var(--space-3);
}

.fallbackLink {
  display: inline-block;
  padding: var(--space-1) var(--space-3);
  border: 1px solid var(--color-brand-primary);
  border-radius: var(--radius-md);
  color: var(--color-brand-primary);
}

@media (max-width: 959px) {
  .layout {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    flex-basis: auto;
    position: static;
  }

  .main {
    width: 100%;
  }
}
</style>
